'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import React, { ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { cn } from '@/shared/utils/cn';
import { getMediaQueries } from '@/shared/utils/breakpoints';
import ArrowRight from '@/shared/components/primitives/svg/arrow-right';
import ArrowLeft from '@/shared/components/primitives/svg/arrow-left';
import {
  EMBLA_NAV_EVENTS,
  readCanScrollNext,
  readCanScrollPrev,
  readSelectedSnap,
  useEmblaSnapshot,
} from '@/shared/hooks/use-embla-snapshot';

interface Props {
  className?: string;
  products: ReactNode[];
  title: string;
  hideSeperators: boolean;
  subTitle?: string;
  perPage?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number; '2xl'?: number };
}

const Carousel = ({
  className,
  title,
  subTitle = '',
  products,
  hideSeperators = false,
  perPage = {},
  ...props
}: Props) => {
  const id = useId();
  const titleId = useId();
  const defaultSizes = useMemo(() => ({ xs: 2, sm: 2, md: 4, lg: 6, xl: 6, '2xl': 6 }), []);
  const grid = useMemo(() => ({ ...defaultSizes, ...perPage }), [defaultSizes, perPage]);

  const gridClasses = {
    'xs-2': 'grid-cols-2',
    'xs-4': 'grid-cols-4',
    'sm-2': 'sm:grid-cols-2',
    'sm-3': 'sm:grid-cols-3',
    'sm-4': 'sm:grid-cols-4',
    'md-2': 'md:grid-cols-2',
    'md-3': 'md:grid-cols-3',
    'md-4': 'md:grid-cols-4',
    'md-5': 'md:grid-cols-5',
    'md-6': 'md:grid-cols-6',
    'lg-2': 'lg:grid-cols-2',
    'lg-3': 'lg:grid-cols-3',
    'lg-4': 'lg:grid-cols-4',
    'lg-5': 'lg:grid-cols-5',
    'lg-6': 'lg:grid-cols-6',
    'xl-2': 'xl:grid-cols-2',
    'xl-3': 'xl:grid-cols-3',
    'xl-4': 'xl:grid-cols-4',
    'xl-5': 'xl:grid-cols-5',
    'xl-6': 'xl:grid-cols-6',
  };

  const xsGridClass = gridClasses[`xs-${grid.xs}` as keyof typeof gridClasses] || 'grid-cols-2';

  const smGridClass = gridClasses[`sm-${grid.sm}` as keyof typeof gridClasses] || 'sm:grid-cols-2';

  const mdGridClass = gridClasses[`md-${grid.md}` as keyof typeof gridClasses] || 'md:grid-cols-4';

  const lgGridClass = gridClasses[`lg-${grid.lg}` as keyof typeof gridClasses] || 'lg:grid-cols-6';

  const xlGridClass = gridClasses[`xl-${grid.xl}` as keyof typeof gridClasses] || 'xl:grid-cols-6';

  const [groupToShow, setGroupToShow] = useState<ReactNode[][]>([]);

  const [carouselRef, api] = useEmblaCarousel({
    loop: true,
    axis: 'x',
  });

  const t = useTranslations('Components.carousel');

  const canScrollPrev = useEmblaSnapshot(api, EMBLA_NAV_EVENTS, readCanScrollPrev, false);
  const canScrollNext = useEmblaSnapshot(api, EMBLA_NAV_EVENTS, readCanScrollNext, false);
  const selectedSnapIndex = useEmblaSnapshot(api, EMBLA_NAV_EVENTS, readSelectedSnap, 0);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  const updateSlidesToShow = useCallback(() => {
    let x = 'xs';

    const mediaQueries = getMediaQueries();

    Object.entries(mediaQueries).forEach(([name, mq]) => {
      if (mq.matches) {
        x = name;
      }
    });

    const perGroup = grid[x as keyof typeof perPage] || 2;

    const p = products.reduce<ReactNode[][]>((batches, _, index) => {
      if (index % perGroup === 0) {
        batches.push([]);
      }

      const product = products[index];

      if (batches[batches.length - 1] && product) {
        batches[batches.length - 1]?.push(product);
      }

      return batches;
    }, []);

    setGroupToShow(p);
  }, [grid, products]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const mediaQueries = getMediaQueries();

    Object.entries(mediaQueries).forEach(([, mq]) => {
      mq.addEventListener('change', updateSlidesToShow);
    });

    const updateFrameId = window.requestAnimationFrame(() => {
      updateSlidesToShow();
    });

    return () => {
      window.cancelAnimationFrame(updateFrameId);
      Object.entries(mediaQueries).forEach(([, mq]) => {
        mq.removeEventListener('change', updateSlidesToShow);
      });
    };
  }, [api, updateSlidesToShow]);
  const isSingleGroup = groupToShow.length === 1;
  return (
    <div
      aria-labelledby={titleId}
      aria-roledescription={t('carousel')}
      className={cn('relative font-sans', className)}
      onKeyDownCapture={handleKeyDown}
      role='region'
      {...props}
    >
      <div className='relative flex items-center justify-center text-white'>
        {!hideSeperators && <div className='hidden flex-1 border-t border-gray-500 md:block' />}
        <h2 className='px-[20px] text-center text-[24px] font-bold' id={titleId}>
          {title}
        </h2>
        {!hideSeperators && <div className='hidden flex-1 border-t border-gray-500 md:block' />}
      </div>

      {subTitle ? <h3 className='text-center text-[18px] text-white'>{subTitle}</h3> : null}

      <div className='static'>
        {!isSingleGroup && (
          <>
            <button
              aria-label={t('previousProducts')}
              className={cn(
                'bg-background-overlay-panel absolute top-1/2 left-[-10px] z-20 flex h-[50px] w-[40px] -translate-y-[150%] transform items-center justify-center',
              )}
              disabled={!canScrollPrev}
              onClick={scrollPrev}
            >
              <ArrowLeft />
              <span className='sr-only'>{t('previousProducts')}</span>
            </button>

            <button
              aria-label={t('nextProducts')}
              className={cn(
                'bg-background-overlay-panel absolute top-1/2 right-[-10px] z-20 flex h-[50px] w-[40px] -translate-y-[150%] transform items-center justify-center',
              )}
              disabled={!canScrollNext}
              onClick={scrollNext}
            >
              <ArrowRight />
              <span className='sr-only'>{t('nextProducts')}</span>
            </button>
          </>
        )}

        <div className='overflow-hidden' ref={carouselRef}>
          <div className='mt-8 mb-8 flex'>
            {groupToShow.map((group, index) => (
              <div
                aria-label={`${index + 1} of ${groupToShow.length}`}
                aria-roledescription='slide'
                className={`grid min-w-0 shrink-0 grow-0 basis-full gap-3 px-2 ${xsGridClass} ${smGridClass} ${mdGridClass} ${lgGridClass} ${xlGridClass}`}
                id={`${id}-slide-${index + 1}`}
                key={index}
                role='group'
              >
                {group.map((item) => item)}
              </div>
            ))}
          </div>
        </div>

        {!isSingleGroup && (
          <div
            aria-label={t('slides')}
            className={cn(
              'no-wrap absolute bottom-1 flex w-full items-center justify-center gap-2',
            )}
            role='tablist'
          >
            {groupToShow.map((_, index) => (
              <button
                aria-controls={`${id}-slide-${index + 1}`}
                aria-label={t('goto', { n: index + 1 })}
                aria-selected={selectedSnapIndex === index}
                className={cn(
                  'h-[20px] w-[20px] rounded-full border-[1px] border-solid border-[#b3b3b3] hover:bg-[#b3b3b3]',
                  selectedSnapIndex === index && 'border-primary bg-primary hover:bg-primary',
                )}
                key={index}
                onClick={() => api?.scrollTo(index)}
                role='tab'
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { Carousel };
